/**
 * DIFFUSION d'un lien de partage — feuille native, presse-papiers, ou rien.
 *
 * Ce module ne CONSTRUIT plus d'URL : depuis la v1.27.0, le lien est créé côté serveur
 * (`/p/<jeton>`, XPHOTO-31) et cette couche ne fait que le porter jusqu'à l'application
 * de destination. L'`absoluteUrl` qui vivait ici — et tout le parti pris « l'URL
 * courante EST la vue » qu'il servait — est mort avec l'ancien bouton « Partager », qui
 * copiait l'adresse de la page.
 *
 * Reste ce qui n'a jamais dépendu de la provenance du lien : la cascade de repli entre
 * les deux API du navigateur, et la distinction entre un échec et une annulation.
 */

/** Ce que le navigateur a effectivement fait du lien (résultat DISCRIMINÉ). */
export type ShareOutcome =
  /** Feuille de partage native honorée (mobile). */
  | { kind: 'shared' }
  /** Lien déposé dans le presse-papiers. */
  | { kind: 'copied' }
  /** L'utilisateur a refermé la feuille de partage : ce n'est PAS une erreur. */
  | { kind: 'cancelled' }
  /** Aucune API disponible (contexte non sécurisé, navigateur ancien) → repli manuel. */
  | { kind: 'unavailable' }
  /** API présente mais en échec (presse-papiers refusé…) → repli manuel. */
  | { kind: 'failed' };

/**
 * Capacités du navigateur, INJECTÉES plutôt que lues globalement : c'est ce qui rend
 * `shareLink` testable sans bricoler `navigator` (et qui documente noir sur blanc les
 * deux seules API dont le partage dépend).
 */
export interface ShareDeps {
  /** `navigator.share` — absent partout sauf mobile (et hors contexte sécurisé). */
  share?: ((data: { title: string; url: string }) => Promise<void>) | undefined;
  /** `navigator.clipboard.writeText` — absent en `http://` (contexte non sécurisé). */
  writeText?: ((text: string) => Promise<void>) | undefined;
}

/**
 * Une annulation utilisateur de la feuille de partage se présente comme un rejet
 * `AbortError`. On la reconnaît par le NOM plutôt que par `instanceof DOMException` :
 * les navigateurs ne rejettent pas tous avec la même classe, et `DOMException`
 * n'existe pas dans tous les environnements d'exécution des tests.
 */
function isUserCancellation(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name?: unknown }).name === 'AbortError'
  );
}

/**
 * Partage `url` : feuille native si le navigateur en a une, sinon presse-papiers,
 * sinon rien (à l'appelant d'offrir le repli manuel).
 *
 * La feuille native est PRIORITAIRE sur le presse-papiers : sur mobile, elle permet
 * d'envoyer le lien directement dans une conversation, là où une copie silencieuse
 * obligerait à ouvrir soi-même l'application de destination.
 *
 * Un échec NON annulé de la feuille native retombe sur le presse-papiers : l'action
 * de l'utilisateur aboutit quand même, au lieu d'un message d'erreur sec.
 */
export async function shareLink(
  url: string,
  title: string,
  deps: ShareDeps,
): Promise<ShareOutcome> {
  if (deps.share !== undefined) {
    try {
      await deps.share({ title, url });
      return { kind: 'shared' };
    } catch (err) {
      // Fermeture de la feuille par l'utilisateur : geste délibéré, aucun message.
      if (isUserCancellation(err)) return { kind: 'cancelled' };
    }
  }
  if (deps.writeText !== undefined) {
    try {
      await deps.writeText(url);
      return { kind: 'copied' };
    } catch {
      return { kind: 'failed' };
    }
  }
  return { kind: 'unavailable' };
}

/**
 * Sonde le navigateur et en dérive les `ShareDeps`.
 *
 * `Navigator.share` et `Navigator.clipboard` sont typés NON optionnels par la lib DOM
 * alors qu'ils manquent bel et bien à l'exécution (`share` sur desktop, `clipboard`
 * hors contexte sécurisé). D'où la sonde `typeof … === 'function'` : sans elle, le
 * partage planterait sur un `undefined is not a function` au lieu d'offrir son repli.
 */
export function browserShareDeps(nav: Navigator): ShareDeps {
  const clipboard: Clipboard | undefined = nav.clipboard;
  return {
    share: typeof nav.share === 'function' ? (data) => nav.share(data) : undefined,
    writeText:
      clipboard !== undefined && typeof clipboard.writeText === 'function'
        ? (text) => clipboard.writeText(text)
        : undefined,
  };
}
